import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { Card, Select, Statistic, Typography } from "antd";
import { BarChart } from "~/components/Charts/BarChart";
import { PieChart } from "~/components/Charts/PieChart";
import { useTranslation } from "react-i18next";
import { formatMoney } from "~/utils";
import { useState } from "react";
import {
  FEES_COLOR,
  LOST_RENT_COLOR,
  OCCUPIED_COLOR,
  OFF_MARKET_COLOR,
  OTHER_REVENUE_COLOR,
  RENOVATION_COLOR,
  RENT_COLOR,
  REPAIRS_COLOR,
  STAY_REVENUE_COLOR,
  VACANT_COLOR,
} from "~/pages/FinancePages/utils/financeColors";

interface MainDashboardProps {
  totalIncome: number;
  totalExpenditure: number;
  netIncome: number;
  monthlyCounts: any; // Adjust type based on your real estate data
  yearlyOccupancyData: number[];
  monthlyOccupancyData: number[];
  barChartData: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      backgroundColor: string;
      borderColor: string;
      borderWidth: number;
    }[];
  };
  occupancyCountBarChartData: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      backgroundColor: string;
      borderColor: string;
      borderWidth: number;
    }[];
  };
  occupancyRateBarChartData: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      backgroundColor: string;
      borderColor: string;
      borderWidth: number;
    }[];
  };
  monthlyRevenueData: number[];
  yearlyRevenueData: number[];
  monthlyExpenseData: number[];
  yearlyExpenseData: number[];
  barChartOptions: {
    scales: {
      y: { beginAtZero: boolean; title: { display: boolean; text: string } };
      x: { title: { display: boolean; text: string } };
    };
  };
  occupancyCountBarChartOptions: {
    scales: {
      y: {
        beginAtZero: boolean;
        max?: number;
        title: { display: boolean; text: string };
      };
      x: { title: { display: boolean; text: string } };
    };
  };
  occupancyRateBarChartOptions: {
    scales: {
      y: {
        beginAtZero: boolean;
        max?: number;
        title: { display: boolean; text: string };
      };
      x: { title: { display: boolean; text: string } };
    };
  };
}

export const MainFinanceDashboard = ({
  totalIncome,
  totalExpenditure,
  netIncome,
  monthlyCounts,
  yearlyOccupancyData,
  monthlyOccupancyData,
  barChartData,
  occupancyCountBarChartData,
  occupancyRateBarChartData,
  barChartOptions,
  occupancyCountBarChartOptions,
  occupancyRateBarChartOptions,
  monthlyRevenueData,
  yearlyRevenueData,
  monthlyExpenseData,
  yearlyExpenseData,
}: MainDashboardProps) => {
  const { t } = useTranslation();
  const [monthly, setMonthly] = useState(1);
  const formatCurrencyValue = (value: number) => formatMoney(value);
  const formatPercentValue = (value: number) => `${value.toFixed(1)}%`;
  const occupancyLabels = [
    t("Occupied") + " " + t("rate"),
    t("Vacant") + " " + t("rate"),
    t("Off_Market") + " " + t("rate"),
    t("Renovation") + " " + t("rate"),
  ];
  const revenueLabels = [
    t("Rent"),
    t("Stay_Revenue"),
    t("Fees_and_Charges"),
    t("Other_Revenue"),
  ];
  const revenueColors = [
    RENT_COLOR,
    STAY_REVENUE_COLOR,
    FEES_COLOR,
    OTHER_REVENUE_COLOR,
  ];
  const hideStaySlice =
    (yearlyRevenueData[1] ?? 0) === 0 && (monthlyRevenueData[1] ?? 0) === 0;
  const pieRevenueData = hideStaySlice
    ? (monthly ? monthlyRevenueData : yearlyRevenueData).filter(
        (_value, index) => index !== 1,
      )
    : monthly
      ? monthlyRevenueData
      : yearlyRevenueData;
  const pieRevenueLabels = hideStaySlice
    ? revenueLabels.filter((_label, index) => index !== 1)
    : revenueLabels;
  const pieRevenueColors = hideStaySlice
    ? revenueColors.filter((_color, index) => index !== 1)
    : revenueColors;
  const expenseLabels = [t("Repairs"), t("Lost_Rent"), t("Renovation")];

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* Pie Chart */}
      <div
        style={{
          flex: "1",
          backgroundColor: "#e0e0e0",
          padding: "10px",
          borderRadius: "8px",
          minHeight: "300px",
        }}
      >
        <Select
          style={{
            minWidth: 100,
          }}
          options={[
            { label: t("Year"), value: 0 },
            {
              label: t("Month"),
              value: 1,
            },
          ]}
          defaultValue={1}
          onChange={(value) => {
            setMonthly(value);
          }}
        />
        <div className="double-pie-container">
          <div>
            <Typography.Text
              strong
              style={{ display: "block", textAlign: "center", marginBottom: 8 }}
            >
              {t("Occupancy")}
            </Typography.Text>
            <PieChart
              data={monthly ? monthlyOccupancyData : yearlyOccupancyData}
              labels={occupancyLabels}
              height={200}
              colors={[
                OCCUPIED_COLOR,
                VACANT_COLOR,
                OFF_MARKET_COLOR,
                RENOVATION_COLOR,
              ]}
              minWidth={200}
              noDataText={t("No_occupancy_data_found")}
              valueFormatter={formatPercentValue}
            />
          </div>
          <div>
            <Typography.Text
              strong
              style={{ display: "block", textAlign: "center", marginBottom: 8 }}
            >
              {t("Total_Income")}
            </Typography.Text>
            <PieChart
              data={pieRevenueData}
              labels={pieRevenueLabels}
              height={200}
              colors={pieRevenueColors}
              noDataText={t("No_income_data_found")}
              minWidth={200}
              valueFormatter={formatCurrencyValue}
            />
          </div>
          <div>
            <Typography.Text
              strong
              style={{ display: "block", textAlign: "center", marginBottom: 8 }}
            >
              {t("Total_Expenditure")}
            </Typography.Text>
            <PieChart
              data={monthly ? monthlyExpenseData : yearlyExpenseData}
              labels={expenseLabels}
              height={200}
              colors={[REPAIRS_COLOR, LOST_RENT_COLOR, RENOVATION_COLOR]}
              noDataText={t("No_expense_data_found")}
              minWidth={200}
              valueFormatter={formatCurrencyValue}
            />
          </div>
        </div>
      </div>
      <br />

      {/* Monthly Income */}
      {/* Bar Chart */}
      <div
        style={{
          overflow: "scroll",
          width: "100%",
        }}
      >
        <div
          style={{
            minWidth: 500,
            height: "70vh",
          }}
        >
          <BarChart
            data={barChartData}
            options={barChartOptions}
            monthlyPropertyCounts={monthlyCounts}
          />
        </div>
      </div>
      <div
        style={{
          overflow: "scroll",
          width: "100%",
          marginTop: "1.5rem",
        }}
      >
        <Typography.Text
          strong
          style={{ display: "block", marginBottom: 8 }}
        >
          {t("Occupancy")} {t("number_or_rate")}
        </Typography.Text>
        <div
          style={{
            minWidth: 500,
            height: "50vh",
            marginBottom: "1.5rem",
          }}
        >
          <Typography.Text
            style={{ display: "block", textAlign: "center", marginBottom: 8 }}
          >
            {t("Occupied")}/{t("Vacant")}/{t("Off_Market")}/{t("Renovation")}{" "}
            {t("number_or_rate")}
          </Typography.Text>
          <BarChart
            data={occupancyCountBarChartData}
            options={occupancyCountBarChartOptions}
            monthlyPropertyCounts={monthlyCounts}
          />
        </div>
        <div
          style={{
            minWidth: 500,
            height: "50vh",
          }}
        >
          <Typography.Text
            style={{ display: "block", textAlign: "center", marginBottom: 8 }}
          >
            {t("Occupied")}/{t("Vacant")}/{t("Off_Market")}/{t("Renovation")}{" "}
            {t("rate")}
          </Typography.Text>
          <BarChart
            data={occupancyRateBarChartData}
            options={occupancyRateBarChartOptions}
            monthlyPropertyCounts={monthlyCounts}
          />
        </div>
      </div>
      {/* <Divider type="vertical" style={{ height: 300 }} /> */}

      {/* Box 3 - Additional Statistics */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 5,
          backgroundColor: "#e0e0e0",
          padding: "10px",
          borderRadius: "8px",
          marginTop: "3rem",
        }}
      >
        <Card variant="borderless" className="finance-card-body">
          <Statistic
            title={t("Net_Income")}
            value={netIncome}
            precision={2}
            valueStyle={{ color: "#3f8600" }}
            prefix={<ArrowUpOutlined />}
          />
        </Card>
        <Card variant="borderless" className="finance-card-body">
          <Statistic
            title={t("Total_Income")}
            value={totalIncome}
            precision={2}
            valueStyle={{ color: "#368ce0" }}
          />
        </Card>
        <Card variant="borderless" className="finance-card-body">
          <Statistic
            title={t("Total_Expenditure")}
            value={totalExpenditure}
            precision={2}
            valueStyle={{ color: "#cf1322" }}
            prefix={<ArrowDownOutlined />}
            // suffix="%"
          />
        </Card>
      </div>

      {/* Other components or boxes go here */}
    </div>
  );
};
